import { useEffect, useId, useRef, useState } from 'react';
import { LogOut } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { authApi } from '../api/auth';
import { useToastStore } from '../store/useToastStore';
import { FloatingMenu } from './FloatingMenu';
import './SidebarUserMenu.css';

interface Props { userName: string; collapsed: boolean; mobileOpen: boolean }

export function SidebarUserMenu({ userName, collapsed, mobileOpen }: Props) {
  const { pathname } = useLocation();
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  const [loggingOut, setLoggingOut] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const logoutRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();
  const showToast = useToastStore(state => state.show);

  useEffect(() => { setAnchor(null); }, [pathname, collapsed, mobileOpen]);
  useEffect(() => { if (anchor) logoutRef.current?.focus({ preventScroll: true }); }, [anchor]);

  const logout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    setAnchor(null);
    try { await authApi.logout(); }
    catch (error) {
      setLoggingOut(false);
      showToast(error instanceof Error ? error.message : '退出登录失败，请重试', 'error');
    }
  };

  return <>
    <div className="sidebar-user">
    <button
      ref={triggerRef}
      type="button"
      className={'sidebar-user-trigger' + (anchor ? ' is-open' : '')}
      title={collapsed ? userName + ' · 账户菜单' : userName}
      aria-label={userName + '，账户菜单'}
      aria-haspopup="menu"
      aria-expanded={!!anchor}
      aria-controls={anchor ? menuId : undefined}
      disabled={loggingOut}
      onClick={event => setAnchor(anchor ? null : event.currentTarget)}
      onKeyDown={event => {
        if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
          event.preventDefault();
          setAnchor(event.currentTarget);
        }
      }}
    >
      <span className="avatar" aria-hidden="true">{userName.charAt(0).toUpperCase()}</span>
      {!collapsed && <span className="sidebar-user-name">{userName}</span>}
    </button>
    {!collapsed && <button type="button" className="sidebar-user-logout" aria-label="退出登录" title="退出登录" disabled={loggingOut} onClick={() => void logout()}><LogOut size={15} aria-hidden="true" /></button>}
    </div>
    <FloatingMenu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)} align="start" minWidth={0}>
      <div id={menuId} className="sidebar-user-menu" role="menu" aria-label="个人账户" onKeyDown={event => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          setAnchor(null);
          triggerRef.current?.focus();
        } else if (event.key === 'Tab') {
          setAnchor(null);
        }
      }}>
        <div className="sidebar-user-menu__name" role="presentation" title={userName}>{userName}</div>
        <button ref={logoutRef} type="button" role="menuitem" className="sidebar-user-menu__logout" disabled={loggingOut} onClick={() => void logout()}>退出登录</button>
      </div>
    </FloatingMenu>
  </>;
}
